/**
 * 充值弹窗内：选中某链后可见停留 ≥10s，按链分别上报 51。
 *
 * 链码以 coboDepositSupported → depositOptions[].chain 为准：
 * TRON | ETH | BSC | POLYGON | ARBITRUM | SOLANA | BASE
 *
 * 同一弹窗会话内每条链最多一次；切链重置计时；关窗清空。
 */
import { trackAnalyticsLog, withDepositFunnelValue } from '@/utils/analytics-track';

const DWELL_MS = 10_000;

/**
 * API `chain` → slug（小写，用于本地 key / 51 后缀）
 * @type {Readonly<Record<string, string>>}
 */
const API_CHAIN_TO_SLUG = Object.freeze({
  TRON: 'tron',
  ETH: 'eth',
  BSC: 'bsc',
  POLYGON: 'polygon',
  ARBITRUM: 'arbitrum',
  SOLANA: 'solana',
  BASE: 'base',
});

/**
 * 非 API chain 码的兜底（connected 钱包用 numeric chainId / 展示名）
 * @type {Readonly<Record<string, string>>}
 */
const ALIAS_TO_SLUG = Object.freeze({
  '1': 'eth',
  ethereum: 'eth',
  '56': 'bsc',
  'bnb smart chain': 'bsc',
  '137': 'polygon',
  matic: 'polygon',
  '42161': 'arbitrum',
  arb: 'arbitrum',
  '8453': 'base',
  tron: 'tron',
  polygon: 'polygon',
  arbitrum: 'arbitrum',
  solana: 'solana',
  base: 'base',
});

/** slug → 本地点分 key（须已在 LA_EVENT_ID_BY_KEY） */
const DWELL_KEY_BY_SLUG = Object.freeze({
  tron: 'deposit.chain_dwell_10s.tron',
  eth: 'deposit.chain_dwell_10s.eth',
  bsc: 'deposit.chain_dwell_10s.bsc',
  polygon: 'deposit.chain_dwell_10s.polygon',
  arbitrum: 'deposit.chain_dwell_10s.arbitrum',
  solana: 'deposit.chain_dwell_10s.solana',
  base: 'deposit.chain_dwell_10s.base',
});

const DWELL_LABEL_BY_SLUG = Object.freeze({
  tron: '在充值界面选择 TRON 链后可见停留超过十秒',
  eth: '在充值界面选择 ETH 链后可见停留超过十秒',
  bsc: '在充值界面选择 BSC 链后可见停留超过十秒',
  polygon: '在充值界面选择 POLYGON 链后可见停留超过十秒',
  arbitrum: '在充值界面选择 ARBITRUM 链后可见停留超过十秒',
  solana: '在充值界面选择 SOLANA 链后可见停留超过十秒',
  base: '在充值界面选择 BASE 链后可见停留超过十秒',
});

/**
 * @param {unknown} raw API chain / chainId / displayName
 * @returns {string} slug 或空串
 */
export function resolveDepositChainDwellSlug(raw) {
  const key = String(raw ?? '').trim();
  if (!key) return '';
  const upper = key.toUpperCase();
  if (API_CHAIN_TO_SLUG[upper]) return API_CHAIN_TO_SLUG[upper];
  const lower = key.toLowerCase();
  if (ALIAS_TO_SLUG[lower]) return ALIAS_TO_SLUG[lower];
  if (ALIAS_TO_SLUG[key]) return ALIAS_TO_SLUG[key];
  return '';
}

let dialogOpen = false;
/** 规范化后的 slug */
let activeSlug = '';
let activeRawChain = '';
let activeAsset = '';
let accruedMs = 0;
let tickStartedAt = 0;
/** @type {ReturnType<typeof setTimeout> | null} */
let timer = null;
/** 本轮弹窗内已上报的 slug */
const firedSlugs = new Set();
let visibilityBound = false;

function clearTimer() {
  if (timer == null) return;
  clearTimeout(timer);
  timer = null;
}

function flushVisibleSlice() {
  if (!tickStartedAt) return;
  accruedMs += Date.now() - tickStartedAt;
  tickStartedAt = 0;
}

function fire() {
  const slug = activeSlug;
  if (!slug || !dialogOpen) return;
  if (firedSlugs.has(slug)) return;
  const key = DWELL_KEY_BY_SLUG[slug];
  if (!key) return;
  firedSlugs.add(slug);
  clearTimer();
  tickStartedAt = 0;
  trackAnalyticsLog({
    key,
    label: DWELL_LABEL_BY_SLUG[slug] || '充值选链后停留超过十秒',
    value: withDepositFunnelValue({
      rail: 'crypto',
      chain: activeRawChain || slug.toUpperCase(),
      chainSlug: slug,
      asset: activeAsset || undefined,
      dwellMs: Math.max(DWELL_MS, Math.round(accruedMs)),
    }),
  });
}

function schedule() {
  clearTimer();
  if (!dialogOpen || !activeSlug) return;
  if (firedSlugs.has(activeSlug)) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  const remain = DWELL_MS - accruedMs;
  if (remain <= 0) {
    fire();
    return;
  }
  tickStartedAt = Date.now();
  timer = setTimeout(() => {
    timer = null;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    flushVisibleSlice();
    if (accruedMs >= DWELL_MS) fire();
    else schedule();
  }, remain);
}

function onVisibility() {
  if (!dialogOpen || !activeSlug || firedSlugs.has(activeSlug)) return;
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible') {
    schedule();
    return;
  }
  flushVisibleSlice();
  clearTimer();
  if (accruedMs >= DWELL_MS) fire();
}

function ensureVisibilityListener() {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', onVisibility);
}

function stopTiming() {
  flushVisibleSlice();
  clearTimer();
  tickStartedAt = 0;
}

/**
 * 充值弹窗开合：关窗停止计时并清空本轮去重。
 * @param {boolean} open
 */
export function setDepositChainDwellDialogOpen(open) {
  dialogOpen = Boolean(open);
  if (!dialogOpen) {
    stopTiming();
    activeSlug = '';
    activeRawChain = '';
    activeAsset = '';
    accruedMs = 0;
    firedSlugs.clear();
    return;
  }
  ensureVisibilityListener();
}

/**
 * 用户选中某充值链后开始（或切换后重置）10s 计时。
 * 入参 chain 优先传 API 的 depositOptions[].chain（如 ARBITRUM）。
 * @param {{ chain?: unknown, asset?: unknown }} [input]
 */
export function armDepositChainDwell(input = {}) {
  const raw = String(input.chain ?? '').trim();
  if (!raw) return;
  const slug = resolveDepositChainDwellSlug(raw);
  if (!slug) return;
  const asset = String(input.asset ?? '').trim();

  // 选链 / 地址展示常早于 setDepositDialogOpen(true)，此处自愈
  if (!dialogOpen) {
    dialogOpen = true;
  }
  ensureVisibilityListener();

  if (slug === activeSlug) {
    activeRawChain = raw;
    if (asset) activeAsset = asset;
    if (!firedSlugs.has(slug) && !timer && !tickStartedAt) schedule();
    return;
  }

  stopTiming();
  activeSlug = slug;
  activeRawChain = raw;
  activeAsset = asset;
  accruedMs = 0;
  if (firedSlugs.has(slug)) return;
  schedule();
}
